import {
  $glimrTagsAtom,
  $ppidAtom,
  $pageViewIdAtom
} from '@client/core/atoms/config.js';
import { $gamTargetingAtom } from '@client/core/atoms/gamTargeting.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { GridSizingHandler } from '@client/core/utils/dom/GridSizingHandler.js';
import { getGlimrTags } from '@client/core/utils/glimr.js';
import type { Event } from '@podium/browser';
import { events } from '@schibsted-nmp/advertising-events';
import {
  DisplayPlacementId,
  hasItems,
  type GamKeyValue,
  type GamConfig
} from '@schibsted-nmp/advertising-shared';

import { debug } from './.debug.js';
import { Api } from './Api/index.js';
import * as EventListeners from './EventListeners/index.js';
import { LazyLoad } from './LazyLoad.js';
import { setTargetingOnSlotOrGlobal } from './targeting.js';

let previousGamTargetingKeys = new Set<string>();

export async function initiateGam(
  gamConfig: GamConfig,
  ...abortSignals: ReadonlyArray<AbortSignal>
) {
  await EventListeners.setup(...abortSignals);

  const googletag = await Api(...abortSignals);

  googletag.pubads().enableSingleRequest();
  googletag.pubads().collapseEmptyDivs(true);
  googletag.pubads().disableInitialLoad();
  googletag.pubads().setCentering(true);

  await LazyLoad.enable(...abortSignals);

  GridSizingHandler.initiate(googletag);

  // Set global targeting
  $ppidAtom.subscribe(initiateGam.onPpidChange);
  $glimrTagsAtom.subscribe(initiateGam.onGlimrTagsChange);
  $pageViewIdAtom.subscribe(initiateGam.onPageViewIdChange);
  $gamTargetingAtom.subscribe(initiateGam.onGamTargetingChange);

  // Enable services now that everything is set up
  googletag.enableServices();

  debug
    .message('Completed initialization', googletag, gamConfig)
    .success((log) => log());
}

export namespace initiateGam {
  export function onPpidChange({ delta }: $ppidAtom.Value) {
    if (!delta) return;

    const ppid = delta.ppId1 || delta.ppId2;
    if (!ppid) return;

    try {
      debug.message`Setting PPID to ${ppid}`().info();
      googletag.pubads().setPublisherProvidedId(ppid);
    } catch (error) {
      debug.message`Failed setting PPID`({ ppid }, error).error();
    }
  }

  export function onGlimrTagsChange(glimrTags: $glimrTagsAtom.Value) {
    const targeting = getGlimrTags(glimrTags);

    if (hasItems(targeting)) {
      debug.message`Setting GLIMR targeting of ${targeting.length} key-value(s)`(
        ...targeting
      ).info((log) => log());

      setTargetingOnSlotOrGlobal({ targeting, global: true }).catch(
        (error: unknown) => {
          debug.message`Failed setting GLIMR targeting`(
            { targeting },
            error
          ).error();
        }
      );
    } else {
      debug.message('No GLIMR targeting to set').info();
    }
  }

  export function onGamTargetingChange(targeting: $gamTargetingAtom.Value) {
    clearRemovedGlobalGamTargetingKeys(targeting);

    setTargetingOnSlotOrGlobal({ targeting, global: true }).catch(
      (error: unknown) => {
        debug.message`Failed setting global targeting`(
          targeting,
          error
        ).error();
      }
    );
  }

  export function onPageViewIdChange(pageViewId: $pageViewIdAtom.Value) {
    if (!pageViewId) return;

    const key = 'PageViewId';
    debug.message`Setting ${key} targeting to ${pageViewId}`().info();

    setTargetingOnSlotOrGlobal({
      targeting: [{ key, value: [pageViewId] }],
      global: true
    }).catch((error: unknown) => {
      debug.message`Failed setting ${key} targeting`(error).error();
    });
  }

  export function handleInCarouselImageUpdate(
    event: Event<{ imageUri: string }>
  ) {
    const { imageUri } =
      events.ITEM_PAGE.INCAROUSEL_IMAGE_UPDATE.getPayload(event);

    if (imageUri) {
      const key = 'itemsecuredthumb';

      debug.message`INCAROUSEL_IMAGE_UPDATE event received, setting ${key} targeting`(
        imageUri
      ).info((log) => log());

      setTargetingOnSlotOrGlobal({
        targeting: [{ key, value: [imageUri] }],
        global: true
      }).catch((error: unknown) => {
        debug.message`Failed setting ${key} targeting`(
          { imageUri },
          error
        ).error();
      });
    }

    // bit of a hack, but incarousel ad needs refreshing after reopening the gallery, this seems to be the less painfull way
    updatePlacementKeyValueById(
      DisplayPlacementId.AdvtIncarousel,
      'status',
      'refresh'
    );
  }
}

function clearRemovedGlobalGamTargetingKeys(
  targeting: ReadonlyArray<GamKeyValue>
) {
  const currentKeys = new Set(targeting.map(({ key }) => key));

  for (const previousKey of previousGamTargetingKeys) {
    if (!currentKeys.has(previousKey)) {
      googletag.pubads().clearTargeting(previousKey);
    }
  }

  previousGamTargetingKeys = currentKeys;
}
